import React, { useState, useEffect, useRef, useCallback } from "react";
import { Col, Container, Row, Card, CardBody, Button, Dropdown, DropdownToggle, DropdownMenu, DropdownItem } from "reactstrap";
import Widget from "./Widgets";
import RecentActivity from "./RecentActivity";
import Section from "./Section";
import UserGrowth from "./UserGrowth";
import RevenueAnalytics from "./RevenueAnalytics";
import SubscriptionDistribution from "./SubscriptionDistribution";
import GeographicDistribution from "./GeographicDistribution";
import MatchAnalytics from "./MatchAnalytics";
import UserDemographics from './UserDemographics';
import ActivityHeatmap from './ActivityHeatmap';
import { dashboardEvents, dashboardWebSocket } from "../../services/websocket/DashboardWebSocket.tsx";
import testWebSocketConnection from "../../utils/websocketTest";
import UserDetailsModal from "../../Components/Common/UserDetailsModal";
import useUserDetails from "../../hooks/useUserDetails";

// Make dashboardEvents available globally
window.dashboardEvents = dashboardEvents;

// Error boundary component to catch WebSocket errors
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error("WebSocket error caught by boundary:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="alert alert-danger m-3">
          <h4 className="alert-heading">WebSocket Connection Error</h4>
          <p>There was an error with the WebSocket connection: {this.state.error?.message || 'Unknown error'}</p>
          <hr />
          <p className="mb-0">
            <Button 
              color="primary" 
              onClick={() => {
                // Reset error state
                this.setState({ hasError: false, error: null });
                // Attempt to reconnect
                if (this.props.onRetry) {
                  this.props.onRetry();
                }
              }}
            >
              Retry Connection
            </Button>
          </p>
        </div>
      );
    }

    return this.props.children;
  }
}

const DashboardEcommerce = () => {
  document.title = "Koottu Dashboard";

  // Add UserDetailsModal state
  const {
    userDetailsModal,
    setUserDetailsModal,
    selectedUser,
    loadingUser,
    fetchUserDetails
  } = useUserDetails();

  const [rightColumn, setRightColumn] = useState(true);
  const [geoHeight, setGeoHeight] = useState('auto');
  const geoRef = useRef(null);
  
  // Add a mounted ref to track component mounting state
  const isMountedRef = useRef(true);

  // Add date range state
  const [dateRange, setDateRange] = useState({
    start: new Date(new Date().setDate(new Date().getDate() - 30)), // Default to last 30 days
    end: new Date()
  });

  // Add group by state
  const [groupBy, setGroupBy] = useState('daily');

  // Dashboard data state
  const [dashboardData, setDashboardData] = useState({
    currentActiveUsers: {
      total: 100,
      android: 60,
      ios: 40,
      trend: 'up',
      peak: 150,
      lastUpdated: new Date().toISOString()
    },
    headerMetrics: {
      total_active_users: 0,
      new_users_today: 0,
      active_subscriptions: 0,
      total_revenue: 0,
      active_sessions: 0,
      match_success_rate: 0,
      trends: {
        total_active_users: 5,
        new_users_today: 10,
        active_subscriptions: 8,
        total_revenue: 12,
        active_sessions: 6,
        match_success_rate: 4
      }
    },
    activities: [
      {
        type: 'video_call',
        user: 'John Doe',
        target_user: 'Jane Smith',
        time: new Date().toISOString(),
        icon: '📹',
        color: '#2196F3',
        status: 'ACCEPTED'
      }
    ],
    matches: [
      {
        first_user: { user_id: 1, name: 'John Doe', profile_pic: '/avatar1.jpg' },
        second_user: { user_id: 2, name: 'Jane Smith', profile_pic: '/avatar2.jpg' },
        color: '#ff4b4b',
        time: new Date().toISOString()
      }
    ],
    subscriptions: [
      {
        user_id: 1,
        user: 'John Doe',
        profile_pic: '/avatar1.jpg',
        plan: 'Premium',
        country: 'United States',
        duration: 6,
        color: '#4b49ac',
        time: new Date().toISOString()
      }
    ],
    topLocations: [
      {
        city: 'New York',
        country: 'United States',
        count: 1500,
        trend: 'up',
        flag: 'us'
      }
    ]
  });

  // WebSocket connection state
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const [error, setError] = useState(null);
  
  // Add safe setState functions that check component mounted state
  const safeSetConnectionStatus = useCallback((status) => {
    if (isMountedRef.current) {
      setConnectionStatus(status);
    }
  }, []);
  
  const safeSetError = useCallback((errorMessage) => {
    if (isMountedRef.current) {
      setError(errorMessage);
    }
  }, []);
  
  const safeSetDashboardData = useCallback((updater) => {
    if (isMountedRef.current) {
      setDashboardData(updater);
    }
  }, []);
  
  const safeSetWaitingTime = useCallback((updater) => {
    if (isMountedRef.current) {
      setWaitingTime(updater);
    }
  }, []);
  
  // Track connection state with refs to persist across renders
  const connectionAttemptsRef = useRef(0);
  const hasInitialConnectionRef = useRef(false);
  const isAuthenticatedRef = useRef(false);
  const isSubscribedRef = useRef(false);
  
  // Constants
  const MAX_CONNECTION_ATTEMPTS = 2;

  // State for tracking waiting time
  const [waitingTime, setWaitingTime] = useState(0);
  const waitingTimerRef = useRef(null);
  
  // Function to start the waiting timer
  const startWaitingTimer = () => {
    // Clear any existing timer
    if (waitingTimerRef.current) {
      clearInterval(waitingTimerRef.current);
    }
    
    // Reset waiting time
    safeSetWaitingTime(0);
    
    // Start a new timer
    waitingTimerRef.current = setInterval(() => {
      safeSetWaitingTime(prev => {
        // If we've been waiting for more than 30 seconds, automatically clear the waiting state
        if (prev >= 30) {
          console.log('Waiting timeout reached, clearing waiting state');
          safeSetConnectionStatus('subscribed');
          safeSetError('No data received within 30 seconds. Dashboard may be using cached data.');
          stopWaitingTimer();
        }
        return prev + 1;
      });
    }, 1000);
  };
  
  // Function to stop the waiting timer
  const stopWaitingTimer = () => {
    if (waitingTimerRef.current) {
      clearInterval(waitingTimerRef.current);
      waitingTimerRef.current = null;
    }
  };

  // Handler for date range changes
  const handleDateRangeChange = (dates) => {
    if (dates.length === 2) {
      setDateRange({
        start: dates[0],
        end: dates[1]
      });
    }
  };

  // Handler for group by changes
  const handleGroupByChange = (group) => {
    setGroupBy(group.toLowerCase());
  };

  // Set up WebSocket connection when the component mounts
  useEffect(() => {
    // Set mounted flag to true
    isMountedRef.current = true;
  
    console.log('Setting up WebSocket connection...');
    connectWebSocket();
    
    // Set up event listeners for WebSocket events
    const setupEventListeners = () => {
      if (window.dashboardEvents) {
        // Save handlers in variables to be able to remove them later
        const headerMetricsHandler = (data) => {
          if (isMountedRef.current) {
            console.log('Received header metrics event:', data);
            safeSetDashboardData(prev => ({
              ...prev,
              headerMetrics: { ...prev.headerMetrics, ...data }
            }));
          }
        };
        
        const activitiesHandler = (data) => {
          if (isMountedRef.current) {
            console.log('Received activities event:', data?.length || 0);
            safeSetDashboardData(prev => ({
              ...prev,
              activities: Array.isArray(data) ? [...data, ...prev.activities].slice(0, 10) : prev.activities
            }));
          }
        };
        
        const matchesHandler = (data) => {
          if (isMountedRef.current) {
            console.log('Received matches event:', data?.length || 0);
            safeSetDashboardData(prev => ({
              ...prev,
              matches: Array.isArray(data) ? [...data, ...prev.matches].slice(0, 10) : prev.matches
            }));
          }
        };
        
        const subscriptionsHandler = (data) => {
          if (isMountedRef.current) {
            console.log('Received subscriptions event:', data?.length || 0);
            safeSetDashboardData(prev => ({
              ...prev,
              subscriptions: Array.isArray(data) ? [...data, ...prev.subscriptions].slice(0, 10) : prev.subscriptions
            }));
          }
        };
        
        const locationsHandler = (data) => {
          if (isMountedRef.current) {
            console.log('Received locations event:', data?.length || 0);
            safeSetDashboardData(prev => ({
              ...prev,
              topLocations: Array.isArray(data) ? data : prev.topLocations
            }));
          }
        };
        
        const statusHandler = (status) => {
          if (isMountedRef.current) {
            console.log('Received status event:', status);
            safeSetConnectionStatus(status);
          }
        };
        
        // Register event listeners
        window.dashboardEvents.on('dashboard:headerMetrics', headerMetricsHandler);
        window.dashboardEvents.on('dashboard:activities', activitiesHandler);
        window.dashboardEvents.on('dashboard:matches', matchesHandler);
        window.dashboardEvents.on('dashboard:subscriptions', subscriptionsHandler);
        window.dashboardEvents.on('dashboard:locations', locationsHandler);
        window.dashboardEvents.on('websocket:status', statusHandler);
        
        // Return cleanup function that removes all listeners
        return () => {
          window.dashboardEvents.off('dashboard:headerMetrics', headerMetricsHandler);
          window.dashboardEvents.off('dashboard:activities', activitiesHandler);
          window.dashboardEvents.off('dashboard:matches', matchesHandler);
          window.dashboardEvents.off('dashboard:subscriptions', subscriptionsHandler);
          window.dashboardEvents.off('dashboard:locations', locationsHandler);
          window.dashboardEvents.off('websocket:status', statusHandler);
        };
      }
      return () => {}; // Return empty cleanup if no dashboard events
    };
    
    // Set up listeners and store cleanup function
    const cleanupListeners = setupEventListeners();
    
    // Clean up function
    return () => {
      console.log('Cleaning up WebSocket connection...');
      
      // Important: Set mounted flag to false first to prevent state updates
      isMountedRef.current = false;
      
      // Clean up event listeners
      cleanupListeners();
      
      // Clear the ping interval
      if (window.dashboardPingInterval) {
        clearInterval(window.dashboardPingInterval);
        window.dashboardPingInterval = null;
      }
      
      // Close the WebSocket if it's still open
      if (window.dashboardWS && window.dashboardWS.readyState === WebSocket.OPEN) {
        window.dashboardWS.close();
      }
      
      // Clear waiting timer if active
      stopWaitingTimer();
    };
  }, []);

  useEffect(() => {
    const updateHeight = () => {
      if (geoRef.current) {
        const height = geoRef.current.clientHeight;
        setGeoHeight(height);
      }
    };

    // Initial measurement
    updateHeight();

    // Update on window resize
    window.addEventListener('resize', updateHeight);
    
    // Initialize tooltips - Removed in favor of Reactstrap's UncontrolledTooltip
    // const tooltipTriggerList = [].slice.call(document.querySelectorAll('[data-bs-toggle="tooltip"]'));
    // tooltipTriggerList.map(function (tooltipTriggerEl) {
    //   return new window.bootstrap.Tooltip(tooltipTriggerEl);
    // });
    
    // Add CSS for spin animation
    const style = document.createElement('style');
    style.innerHTML = `
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
      .spin {
        animation: spin 1.5s linear infinite;
      }
      .websocket-status-icon {
        transition: all 0.3s ease;
      }
      .websocket-status-icon:hover {
        transform: scale(1.1);
        box-shadow: 0 0 5px rgba(0,0,0,0.2);
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      window.removeEventListener('resize', updateHeight);
      
      // Dispose tooltips
      // tooltipTriggerList.forEach(tooltipTriggerEl => {
      //   const tooltip = window.bootstrap.Tooltip.getInstance(tooltipTriggerEl);
      //   if (tooltip) {
      //     tooltip.dispose();
      //   }
      // });
      
      // Remove the style element
      document.head.removeChild(style);
    };
  }, []);

  const toggleRightColumn = () => {
    setRightColumn(!rightColumn);
  };

  // Function to handle WebSocket connection - only called once
  const connectWebSocket = () => {
    // Only attempt initial connection once
    if (hasInitialConnectionRef.current) {
      console.log('Initial connection already attempted, skipping duplicate call');
      return;
    }
    
    hasInitialConnectionRef.current = true;
    
    // Limit connection attempts to prevent cycling
    if (connectionAttemptsRef.current >= MAX_CONNECTION_ATTEMPTS) {
      console.warn(`Maximum connection attempts (${MAX_CONNECTION_ATTEMPTS}) reached. Stopping reconnection attempts.`);
      safeSetError(`Failed to establish a stable WebSocket connection after ${MAX_CONNECTION_ATTEMPTS} attempts. Please refresh the page.`);
      safeSetConnectionStatus('connection failed');
      return;
    }
    
    connectionAttemptsRef.current++;
    console.log(`Attempting to connect to WebSocket (attempt ${connectionAttemptsRef.current}/${MAX_CONNECTION_ATTEMPTS})...`);
    
    // Check if WebSocket URL is configured
    const wsUrl = process.env.REACT_APP_WEBSOCKET_URL;
    console.log('WebSocket URL from env:', wsUrl);
    
    if (!wsUrl) {
      console.error('WebSocket URL is not configured in environment variables');
      safeSetError('WebSocket URL is not configured. Please check your environment variables.');
      safeSetConnectionStatus('configuration error');
      return;
    }
    
    // Instead of using the dashboardWebSocket singleton, create a direct WebSocket connection
    // This follows the same pattern as the successful test method
    try {
      // Create a new WebSocket connection
      const ws = new WebSocket(wsUrl);
      
      // Connection opened
      ws.addEventListener('open', (event) => {
        console.log('WebSocket connection established successfully!', event);
        safeSetConnectionStatus('connecting');
        
        // Emit event for RecentActivity component
        if (window.dashboardEvents) {
          window.dashboardEvents.emit('websocket:connecting');
        }
        
        // Wait for the server to send the connection_established message
        // Authentication will be handled in the message handler
      });
      
      // Listen for messages
      ws.addEventListener('message', (event) => {
        console.log('Message from server:', event.data);
        
        try {
          const data = JSON.parse(event.data);
          console.log('Parsed message data:', data);
          
          // Check if the message has a data property
          if (data.data) {
            console.log('Message contains data property:', data.data);
          }
          
          // Check if the message has a payload property
          if (data.payload) {
            console.log('Message contains payload property:', data.payload);
          }
          
          // Handle different message types
          if (data.type === 'connection_established') {
            console.log('Connection established, sending authentication message...');
            safeSetConnectionStatus('connected');
            safeSetError(null);
            
            // Emit event for RecentActivity component
            if (window.dashboardEvents) {
              window.dashboardEvents.emit('websocket:connected');
            }
            
            // Send authentication message
            setTimeout(() => {
              try {
                const authMessage = {
                  type: 'authenticate',
                  payload: {
                    apiKey: 'koottu-dashboard-secret-key-2024'
                  }
                };
                
                console.log('Authentication message:', JSON.stringify(authMessage));
                ws.send(JSON.stringify(authMessage));
              } catch (error) {
                console.error('Error sending authentication message:', error);
                safeSetError(`Authentication failed: ${error.message}`);
              }
            }, 500);
          }
          else if (data.type === 'authentication_successful' || data.type === 'authenticated' || data.type === 'auth_success') {
            console.log('Authentication successful');
            isAuthenticatedRef.current = true;
            safeSetConnectionStatus('authenticated');
            safeSetError(null);
            
            // Emit event for RecentActivity component
            if (window.dashboardEvents) {
              window.dashboardEvents.emit('websocket:authenticated');
            }
            
            // Send subscription message
            setTimeout(() => {
              try {
                console.log('Sending subscription message...');
                const subscribeMessage = {
                  type: 'subscribe',
                  payload: {
                    metrics: [
                      'active_users',
                      'recent_activities',
                      'recent_matches',
                      'recent_subscriptions',
                      'top_locations',
                      'header_metrics',
                      'user_growth',
                      'match_analytics',
                      'revenue_analytics',
                      'user_demographics',
                      'age_distribution',
                      'gender_distribution'
                    ],
                    filters: {
                      start_date: dateRange?.start?.toISOString(),
                      end_date: dateRange?.end?.toISOString(),
                      group_by: groupBy
                    }
                  }
                };
                
                console.log('Subscription message:', JSON.stringify(subscribeMessage));
                ws.send(JSON.stringify(subscribeMessage));
                
                // Set status to waiting for updates
                safeSetConnectionStatus('waiting for updates');
                safeSetError('Subscribed successfully. Waiting for initial data...');
                startWaitingTimer();
                
                // Request initial data immediately after subscribing
                setTimeout(() => {
                  try {
                    console.log('Requesting initial data...');
                    const requestDataMessage = {
                      type: 'request_data',
                      payload: {
                        metrics: [
                          'active_users',
                          'recent_activities',
                          'recent_matches',
                          'recent_subscriptions',
                          'top_locations',
                          'header_metrics',
                          'user_growth',
                          'match_analytics',
                          'revenue_analytics',
                          'user_demographics',
                          'age_distribution',
                          'gender_distribution'
                        ],
                        filters: {
                          start_date: dateRange?.start?.toISOString(),
                          end_date: dateRange?.end?.toISOString(),
                          group_by: groupBy
                        }
                      }
                    };
                    
                    console.log('Request data message:', JSON.stringify(requestDataMessage));
                    ws.send(JSON.stringify(requestDataMessage));
                  } catch (error) {
                    console.error('Error requesting initial data:', error);
                  }
                }, 1000);
              } catch (error) {
                console.error('Error sending subscription message:', error);
                safeSetError(`Subscription failed: ${error.message}`);
              }
            }, 1000);
          } 
          else if (data.type === 'subscription_successful' || data.type === 'subscribed' || data.type === 'subscribe_success') {
            console.log('Subscription successful');
            isSubscribedRef.current = true;
            safeSetConnectionStatus('subscribed');
            safeSetError(null);
            stopWaitingTimer();
          }
          // Handle specific data update types
          else if (data.type === 'active_users') {
            console.log('Received active users update:', data);
            if (connectionStatus === 'waiting for updates') {
              safeSetConnectionStatus('subscribed');
              safeSetError(null);
              stopWaitingTimer();
            }
            // Process the data
            const processedData = data.data || data.payload || {};
            dashboardEvents.emit('dashboard:currentActiveUsers', processedData);
          }
          else if (data.type === 'recent_activities') {
            console.log('Received recent activities update:', data);
            if (connectionStatus === 'waiting for updates') {
              safeSetConnectionStatus('subscribed');
              safeSetError(null);
              stopWaitingTimer();
            }
            // Process the data
            const processedData = data.data || data.payload || [];
            dashboardEvents.emit('dashboard:activities', processedData);
          }
          else if (data.type === 'recent_matches') {
            console.log('Received recent matches update:', data);
            if (connectionStatus === 'waiting for updates') {
              safeSetConnectionStatus('subscribed');
              safeSetError(null);
              stopWaitingTimer();
            }
            // Process the data
            const processedData = data.data || data.payload || [];
            dashboardEvents.emit('dashboard:matches', processedData);
          }
          else if (data.type === 'recent_subscriptions') {
            console.log('Received recent subscriptions update:', data);
            if (connectionStatus === 'waiting for updates') {
              safeSetConnectionStatus('subscribed');
              safeSetError(null);
              stopWaitingTimer();
            }
            // Process the data
            const processedData = data.data || data.payload || [];
            dashboardEvents.emit('dashboard:subscriptions', processedData);
          }
          else if (data.type === 'top_locations') {
            console.log('Received top locations update:', data);
            if (connectionStatus === 'waiting for updates') {
              safeSetConnectionStatus('subscribed');
              safeSetError(null);
              stopWaitingTimer();
            }
            // Process the data
            const processedData = data.data || data.payload || [];
            dashboardEvents.emit('dashboard:locations', processedData);
          }
          else if (data.type === 'header_metrics') {
            console.log('Received header metrics update:', data);
            if (connectionStatus === 'waiting for updates') {
              safeSetConnectionStatus('subscribed');
              safeSetError(null);
              stopWaitingTimer();
            }
            // Process the data
            const processedData = data.data || data.payload || {};
            dashboardEvents.emit('dashboard:headerMetrics', processedData);
          }
          else if (data.type === 'user_growth') {
            console.log('Received user growth update:', data);
            if (connectionStatus === 'waiting for updates') {
              safeSetConnectionStatus('subscribed');
              safeSetError(null);
              stopWaitingTimer();
            }
            // Process the data
            const processedData = data.data || data.payload || {};
            dashboardEvents.emit('dashboard:userGrowth', processedData);
          }
          else if (data.type === 'match_analytics') {
            console.log('Received match analytics update:', data);
            if (connectionStatus === 'waiting for updates') {
              safeSetConnectionStatus('subscribed');
              safeSetError(null);
              stopWaitingTimer();
            }
            // Process the data
            const processedData = data.data || data.payload || {};
            dashboardEvents.emit('dashboard:matchAnalytics', processedData);
          }
          else if (data.type === 'revenue_analytics') {
            console.log('Received revenue analytics update:', data);
            if (connectionStatus === 'waiting for updates') {
              safeSetConnectionStatus('subscribed');
              safeSetError(null);
              stopWaitingTimer();
            }
            // Process the data
            const processedData = data.data || data.payload || {};
            dashboardEvents.emit('dashboard:revenueAnalytics', processedData);
          }
          else if (data.type === 'user_demographics') {
            console.log('Received user demographics update:', data);
            if (connectionStatus === 'waiting for updates') {
              safeSetConnectionStatus('subscribed');
              safeSetError(null);
              stopWaitingTimer();
            }
            // Process the data
            const processedData = data.data || data.payload || {};
            dashboardEvents.emit('dashboard:userDemographics', processedData);
          }
          else if (data.type === 'age_distribution') {
            console.log('Received age distribution update:', data);
            if (connectionStatus === 'waiting for updates') {
              safeSetConnectionStatus('subscribed');
              safeSetError(null);
              stopWaitingTimer();
            }
            // Process the data
            const processedData = data.data || data.payload || {};
            dashboardEvents.emit('dashboard:ageDistribution', processedData);
          }
          else if (data.type === 'gender_distribution') {
            console.log('Received gender distribution update:', data);
            if (connectionStatus === 'waiting for updates') {
              safeSetConnectionStatus('subscribed');
              safeSetError(null);
              stopWaitingTimer();
            }
            // Process the data
            const processedData = data.data || data.payload || {};
            dashboardEvents.emit('dashboard:genderDistribution', processedData);
          }
          else if (data.type === 'geographic_distribution') {
            console.log('Received geographic distribution update:', data);
            if (connectionStatus === 'waiting for updates') {
              safeSetConnectionStatus('subscribed');
              safeSetError(null);
              stopWaitingTimer();
            }
            // Process the data
            const processedData = data.data || data.payload || {};
            dashboardEvents.emit('dashboard:geographicDistribution', processedData);
          }
          else if (data.type === 'metric_update' || data.type === 'update') {
            console.log('Received metric update:', data);
            // Clear any waiting messages once we get data
            if (connectionStatus === 'waiting for updates') {
              safeSetConnectionStatus('subscribed');
              safeSetError(null);
              stopWaitingTimer();
            }
            handleMetricUpdate(data);
          }
          else if (data.type === 'error') {
            console.error('Received error from server:', data);
            safeSetError(`Server error: ${data.message || 'Unknown error'}`);
          }
          else if (data.type === 'pong' || data.type === 'ping_response') {
            console.log('Received ping response');
            // No need to update UI for pings
          }
          else {
            console.log('Received unknown message type:', data.type);
            // Try to handle the data anyway in case it contains useful information
            if (data.data) {
              handleMetricUpdate({ data: data.data });
            } else if (data.payload) {
              handleMetricUpdate({ data: data.payload });
            } else {
              // Try to handle the message directly
              handleMetricUpdate(data);
            }
          }
        } catch (error) {
          console.error('Error parsing message:', error);
        }
      });
      
      // Connection closed
      ws.addEventListener('close', (event) => {
        console.log('WebSocket connection closed:', event);
        console.log('Close event code:', event.code);
        console.log('Close event reason:', event.reason);
        console.log('Close event was clean:', event.wasClean);
        
        safeSetConnectionStatus('closed');
        
        // Emit event for RecentActivity component
        if (window.dashboardEvents) {
          window.dashboardEvents.emit('websocket:disconnected');
        }
        
        // Clean up
        hasInitialConnectionRef.current = false;
        
        // If this wasn't a clean close and we haven't reached the maximum connection attempts,
        // try to reconnect after a delay
        if (!event.wasClean && connectionAttemptsRef.current < MAX_CONNECTION_ATTEMPTS) {
          console.log('Connection closed unexpectedly, attempting to reconnect...');
          
          // Wait a bit before reconnecting
          setTimeout(() => {
            // Only reconnect if we're still mounted
            if (connectionAttemptsRef.current < MAX_CONNECTION_ATTEMPTS) {
              console.log('Attempting to reconnect...');
              connectWebSocket();
            }
          }, 5000); // Wait 5 seconds before reconnecting
        } else if (connectionAttemptsRef.current >= MAX_CONNECTION_ATTEMPTS) {
          console.log('Maximum connection attempts reached, not reconnecting');
          safeSetError('Maximum connection attempts reached. Please refresh the page to try again.');
        }
      });
      
      // Connection error
      ws.addEventListener('error', (event) => {
        console.error('WebSocket error:', event);
        safeSetError('WebSocket connection error');
        safeSetConnectionStatus('error');
        
        // Emit event for RecentActivity component
        if (window.dashboardEvents) {
          window.dashboardEvents.emit('websocket:disconnected');
          window.dashboardEvents.emit('websocket:error', 'WebSocket connection error');
        }
      });
      
      // Store the WebSocket instance
      window.dashboardWS = ws;
      
      // Set up ping interval to keep connection alive
      const pingInterval = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          try {
            console.log('Sending ping to keep connection alive');
            ws.send(JSON.stringify({ 
              type: 'ping', 
              payload: { 
                timestamp: Date.now() 
              }
            }));
          } catch (error) {
            console.error('Error sending ping:', error);
            
            // If we can't send a ping, the connection might be dead
            // Try to reconnect
            if (ws.readyState !== WebSocket.OPEN) {
              console.log('WebSocket not open, clearing ping interval');
              clearInterval(pingInterval);
              
              // Emit disconnected event
              if (window.dashboardEvents) {
                window.dashboardEvents.emit('websocket:disconnected');
              }
              
              // Try to reconnect if we haven't reached the maximum attempts
              if (connectionAttemptsRef.current < MAX_CONNECTION_ATTEMPTS) {
                console.log('Attempting to reconnect after ping failure...');
                setTimeout(connectWebSocket, 5000);
              }
            }
          }
        } else {
          console.log('WebSocket not open, clearing ping interval');
          clearInterval(pingInterval);
          
          // Try to reconnect if we haven't reached the maximum attempts
          if (connectionAttemptsRef.current < MAX_CONNECTION_ATTEMPTS) {
            console.log('Attempting to reconnect after connection closed...');
            setTimeout(connectWebSocket, 5000);
          }
        }
      }, 30000);
      
      // Store the ping interval so we can clear it later
      window.dashboardPingInterval = pingInterval;
      
    } catch (error) {
      console.error('Error creating WebSocket:', error);
      safeSetError(`Failed to create WebSocket: ${error.message}`);
      safeSetConnectionStatus('connection error');
    }
  };
  
  // Function to handle metric updates
  const handleMetricUpdate = (data) => {
    console.log('Handling metric update with data:', data);
    
    // Only update state if the component is still mounted
    if (!isMountedRef.current) {
      console.log('Component unmounted, ignoring metric update');
      return;
    }
    
    // If we're still waiting for updates, clear the waiting state
    if (connectionStatus === 'waiting for updates') {
      console.log('Received data while waiting for updates, updating connection status');
      safeSetConnectionStatus('subscribed');
      safeSetError(null);
      stopWaitingTimer();
    }
    
    // Check different possible data structures
    let metricData = null;
    
    if (data.data) {
      console.log('Using data.data for metrics');
      metricData = data.data;
    } else if (data.payload) {
      console.log('Using data.payload for metrics');
      metricData = data.payload;
    } else {
      console.log('No data or payload property found, using data directly');
      metricData = data;
    }
    
    if (!metricData) {
      console.error('No metric data found in update');
      return;
    }
    
    console.log('Processing metric data:', metricData);
    
    // Process different metric types
    if (metricData.active_users) {
      console.log('Received active users update:', metricData.active_users);
      safeSetDashboardData(prev => ({
        ...prev,
        currentActiveUsers: metricData.active_users
      }));
      
      // Update global fallback data
      if (!window.dashboardData) window.dashboardData = {};
      window.dashboardData.activeUsers = metricData.active_users;
      
      // Emit event for components
      if (window.dashboardEvents) {
        window.dashboardEvents.emit('dashboard:activeUsers', metricData.active_users);
      }
    }
    
    if (metricData.activities) {
      console.log('Received activities update:', metricData.activities?.length || 0);
      // Process activities to remove avatar URLs
      const processedActivities = metricData.activities.map(activity => {
        const processed = { ...activity };
        if (processed.avatar_url) delete processed.avatar_url;
        if (processed.target_avatar_url) delete processed.target_avatar_url;
        return processed;
      });
      
      safeSetDashboardData(prev => ({
        ...prev,
        activities: [...processedActivities, ...prev.activities].slice(0, 10)
      }));
      
      // Update global fallback data
      if (!window.dashboardData) window.dashboardData = {};
      window.dashboardData.activities = processedActivities;
      
      // Emit event for components
      if (window.dashboardEvents) {
        window.dashboardEvents.emit('dashboard:activities', processedActivities);
      }
    }
    
    if (metricData.matches) {
      console.log('Received matches update:', metricData.matches?.length || 0);
      // Process matches to remove avatar URLs
      const processedMatches = metricData.matches.map(match => {
        const processed = { ...match };
        if (processed.user1_avatar) delete processed.user1_avatar;
        if (processed.user2_avatar) delete processed.user2_avatar;
        return processed;
      });
      
      safeSetDashboardData(prev => ({
        ...prev,
        matches: [...processedMatches, ...prev.matches].slice(0, 10)
      }));
      
      // Update global fallback data
      if (!window.dashboardData) window.dashboardData = {};
      window.dashboardData.matches = processedMatches;
      
      // Emit event for components
      if (window.dashboardEvents) {
        window.dashboardEvents.emit('dashboard:matches', processedMatches);
      }
    }
    
    if (metricData.subscriptions) {
      console.log('Received subscriptions update:', metricData.subscriptions?.length || 0);
      // Process subscriptions to remove avatar URLs
      const processedSubscriptions = metricData.subscriptions.map(subscription => {
        const processed = { ...subscription };
        if (processed.avatar_url) delete processed.avatar_url;
        return processed;
      });
      
      safeSetDashboardData(prev => ({
        ...prev,
        subscriptions: [...processedSubscriptions, ...prev.subscriptions].slice(0, 10)
      }));
      
      // Update global fallback data
      if (!window.dashboardData) window.dashboardData = {};
      window.dashboardData.subscriptions = processedSubscriptions;
      
      // Emit event for components
      if (window.dashboardEvents) {
        window.dashboardEvents.emit('dashboard:subscriptions', processedSubscriptions);
      }
    }
    
    if (metricData.locations) {
      console.log('Received locations update:', metricData.locations?.length || 0);
      safeSetDashboardData(prev => ({
        ...prev,
        topLocations: metricData.locations
      }));
      
      // Update global fallback data
      if (!window.dashboardData) window.dashboardData = {};
      window.dashboardData.locations = metricData.locations;
      
      // Emit event for components
      if (window.dashboardEvents) {
        window.dashboardEvents.emit('dashboard:locations', metricData.locations);
      }
    }
    
    if (metricData.header_metrics) {
      console.log('Received header metrics update:', metricData.header_metrics);
      safeSetDashboardData(prev => ({
        ...prev,
        headerMetrics: { ...prev.headerMetrics, ...metricData.header_metrics }
      }));
      
      // Update global fallback data
      if (!window.dashboardData) window.dashboardData = {};
      window.dashboardData.headerMetrics = metricData.header_metrics;
      
      // Emit event for components
      if (window.dashboardEvents) {
        window.dashboardEvents.emit('dashboard:headerMetrics', metricData.header_metrics);
      }
    }
    
    // Handle analytics data for CORS fallbacks
    if (metricData.user_growth) {
      console.log('Received user growth data:', metricData.user_growth);
      if (!window.dashboardData) window.dashboardData = {};
      window.dashboardData.userGrowth = metricData.user_growth;
      
      // Emit event for components
      if (window.dashboardEvents) {
        window.dashboardEvents.emit('dashboard:userGrowth', metricData.user_growth);
      }
    }
    
    if (metricData.match_analytics) {
      console.log('Received match analytics data:', metricData.match_analytics);
      if (!window.dashboardData) window.dashboardData = {};
      window.dashboardData.matchAnalytics = metricData.match_analytics;
      
      // Emit event for components
      if (window.dashboardEvents) {
        window.dashboardEvents.emit('dashboard:matchAnalytics', metricData.match_analytics);
      }
    }
    
    if (metricData.revenue_analytics) {
      console.log('Received revenue analytics data:', metricData.revenue_analytics);
      if (!window.dashboardData) window.dashboardData = {};
      window.dashboardData.revenueAnalytics = metricData.revenue_analytics;
      
      // Emit event for components
      if (window.dashboardEvents) {
        window.dashboardEvents.emit('dashboard:revenueAnalytics', metricData.revenue_analytics);
      }
    }
    
    if (metricData.user_demographics) {
      console.log('Received user demographics data:', metricData.user_demographics);
      if (!window.dashboardData) window.dashboardData = {};
      window.dashboardData.userDemographics = metricData.user_demographics;
      
      // Emit event for components
      if (window.dashboardEvents) {
        window.dashboardEvents.emit('dashboard:userDemographics', metricData.user_demographics);
      }
    }
    
    if (metricData.age_distribution) {
      console.log('Received age distribution data:', metricData.age_distribution);
      if (!window.dashboardData) window.dashboardData = {};
      window.dashboardData.ageDistribution = metricData.age_distribution;
      
      // Emit event for components
      if (window.dashboardEvents) {
        window.dashboardEvents.emit('dashboard:ageDistribution', metricData.age_distribution);
      }
    }
    
    if (metricData.gender_distribution) {
      console.log('Received gender distribution data:', metricData.gender_distribution);
      if (!window.dashboardData) window.dashboardData = {};
      window.dashboardData.genderDistribution = metricData.gender_distribution;
      
      // Emit event for components
      if (window.dashboardEvents) {
        window.dashboardEvents.emit('dashboard:genderDistribution', metricData.gender_distribution);
      }
    }
    
    if (metricData.geographic_distribution) {
      console.log('Received geographic distribution data:', metricData.geographic_distribution);
      if (!window.dashboardData) window.dashboardData = {};
      window.dashboardData.geographicDistribution = metricData.geographic_distribution;
      
      // Emit event for components
      if (window.dashboardEvents) {
        window.dashboardEvents.emit('dashboard:geographicDistribution', metricData.geographic_distribution);
      }
    }
    
    if (metricData.subscription_distribution) {
      console.log('Received subscription distribution data:', metricData.subscription_distribution);
      if (!window.dashboardData) window.dashboardData = {};
      window.dashboardData.subscriptionDistribution = metricData.subscription_distribution;
      
      // Emit event for components
      if (window.dashboardEvents) {
        window.dashboardEvents.emit('dashboard:subscriptionDistribution', metricData.subscription_distribution);
      }
    }
  };

  // Function to authenticate WebSocket
  const authenticateWebSocket = () => {
    if (!dashboardWebSocket.webSocket) {
      console.error('Cannot authenticate: WebSocket not connected');
      return;
    }
    
    console.log('Authenticating WebSocket connection...');
    try {
      dashboardWebSocket.send({
        type: 'authenticate',
        payload: {
          apiKey: 'koottu-dashboard-secret-key-2024'
        }
      });
    } catch (error) {
      console.error('Error sending authentication message:', error);
      safeSetError(`Authentication failed: ${error.message}`);
    }
  };
  
  // Function to subscribe to WebSocket metrics
  const subscribeToMetrics = () => {
    if (!dashboardWebSocket.webSocket) {
      console.error('Cannot subscribe to metrics: WebSocket not connected');
      return;
    }
    
    console.log('Subscribing to dashboard metrics...');
    
    // Subscribe to metrics
    dashboardWebSocket.send({
      type: 'subscribe',
      metrics: [
        'active_users',
        'recent_activities',
        'recent_matches',
        'recent_subscriptions',
        'top_locations',
        'header_metrics',
        'user_growth',
        'match_analytics',
        'revenue_analytics',
        'user_demographics',
        'age_distribution',
        'gender_distribution'
      ],
      filters: {
        start_date: dateRange?.start?.toISOString(),
        end_date: dateRange?.end?.toISOString(),
        group_by: groupBy
      }
    });
    
    // Mark as subscribed
    isSubscribedRef.current = true;
    console.log('Subscribed to dashboard metrics');
  };

  // Function to request initial data
  const requestInitialData = () => {
    console.log('Requesting initial WebSocket data...');
    dashboardWebSocket.send({
      type: 'request_data',
      payload: {
        metrics: [
          'active_users',
          'header_metrics',
          'recent_activities',
          'recent_matches',
          'recent_subscriptions',
          'top_locations',
          'user_growth',
          'match_analytics',
          'revenue_analytics',
          'user_demographics',
          'age_distribution',
          'gender_distribution'
        ],
        filters: {
          start_date: dateRange?.start?.toISOString(),
          end_date: dateRange?.end?.toISOString(),
          group_by: groupBy
        }
      }
    });
  };

  // Add handlers for geographic distribution data
  dashboardEvents.on('dashboard:locations', (locations) => {
    console.log('Received locations via WebSocket:', locations?.length || 0);
    if (locations && locations.length > 0) {
      // Update local state
      safeSetDashboardData(prev => ({
        ...prev,
        topLocations: locations // Replace locations as it's a complete list
      }));
      
      // Update global fallback data
      if (!window.dashboardData) window.dashboardData = {};
      window.dashboardData.locations = locations;
    }
  });
  
  // Add handler for geographic distribution data
  dashboardEvents.on('dashboard:geographicDistribution', (data) => {
    console.log('Received geographic distribution data via WebSocket:', data);
    if (data) {
      // Update global fallback data
      if (!window.dashboardData) window.dashboardData = {};
      window.dashboardData.geographicDistribution = data;
    }
  });

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <ErrorBoundary onRetry={() => {
            // Reset connection state
            hasInitialConnectionRef.current = false;
            connectionAttemptsRef.current = 0;
            // Attempt to reconnect
            connectWebSocket();
          }}>
            {/* User Details Modal at the top level */}
            <UserDetailsModal
              isOpen={userDetailsModal}
              toggle={() => setUserDetailsModal(!userDetailsModal)}
              user={selectedUser}
              loading={loadingUser}
              getImageUrl={(url) => {
                if (!url) return '/images/default-avatar.png';
                if (url.startsWith('http')) return url;
                const baseUrl = process.env.REACT_APP_MEDIA_URL?.replace(/\/$/, '') || '';
                const imagePath = url.replace(/^\//, '');
                return `${baseUrl}/${imagePath}`;
              }}
            />
            
            <Row>
              {/* Main Content Area */}
              <Col xl={rightColumn ? 9 : 12}>
                <div className="h-100">
                  {/* Header Section */}
                  <Section 
                    rightClickBtn={toggleRightColumn}
                    dateRange={dateRange}
                    onDateRangeChange={handleDateRangeChange}
                    selectedGroup={groupBy}
                    onGroupChange={handleGroupByChange}
                    connectionStatus={connectionStatus}
                    onRequestData={() => {
                      console.log('Manual data request/reconnect attempt');
                      
                      if (connectionStatus === 'subscribed' || connectionStatus === 'connected' || connectionStatus === 'authenticated') {
                        // If already connected, just request new data
                        if (window.dashboardWS && window.dashboardWS.readyState === WebSocket.OPEN) {
                          try {
                            const requestDataMessage = {
                              type: 'request_data',
                              payload: {
                                metrics: [
                                  'active_users',
                                  'recent_activities',
                                  'recent_matches',
                                  'recent_subscriptions',
                                  'top_locations',
                                  'header_metrics',
                                  'user_growth',
                                  'match_analytics',
                                  'revenue_analytics',
                                  'user_demographics',
                                  'age_distribution',
                                  'gender_distribution'
                                ],
                                filters: {
                                  start_date: dateRange?.start?.toISOString(),
                                  end_date: dateRange?.end?.toISOString(),
                                  group_by: groupBy
                                }
                              }
                            };
                            
                            console.log('Request data message:', JSON.stringify(requestDataMessage));
                            window.dashboardWS.send(JSON.stringify(requestDataMessage));
                            
                            // Set status to waiting for updates
                            safeSetConnectionStatus('waiting for updates');
                            startWaitingTimer();
                          } catch (error) {
                            console.error('Error requesting data:', error);
                          }
                        }
                      } else {
                        // If not connected, try to reconnect
                        // Stop the waiting timer
                        stopWaitingTimer();
                        
                        // Close existing WebSocket if it exists
                        if (window.dashboardWS) {
                          try {
                            window.dashboardWS.close();
                          } catch (error) {
                            console.error('Error closing existing WebSocket:', error);
                          }
                          window.dashboardWS = null;
                        }
                        
                        // Clear existing ping interval
                        if (window.dashboardPingInterval) {
                          clearInterval(window.dashboardPingInterval);
                          window.dashboardPingInterval = null;
                        }
                        
                        // Reset connection state
                        hasInitialConnectionRef.current = false;
                        connectionAttemptsRef.current = 0;
                        
                        // Wait for resources to clean up
                        setTimeout(() => {
                          // Try to connect again
                          connectWebSocket();
                          safeSetConnectionStatus('connecting');
                        }, 1000);
                      }
                    }}
                    waitingTime={waitingTime}
                  />
                  
                  {/* KPI Widgets */}
                  <Row>
                    <Widget headerMetrics={dashboardData.headerMetrics} />
                  </Row>

                  {/* User Growth & Revenue Analytics - Side by side */}
                  <Row className="mb-3">
                    <Col xl={6}>
                      <UserGrowth 
                        dateRange={dateRange}
                        groupBy={groupBy}
                      />
                    </Col>
                    <Col xl={6}>
                      <RevenueAnalytics 
                        dateRange={dateRange}
                        groupBy={groupBy}
                      />
                    </Col>
                  </Row>

                  {/* Geographic Distribution & Subscription Distribution */}
                  <Row className="mb-3">
                    <Col xl={8} className="d-flex">
                      <div className="w-100">
                        <GeographicDistribution dateRange={dateRange} />
                      </div>
                    </Col>
                    <Col xl={4} className="d-flex">
                      <div className="w-100">
                        <SubscriptionDistribution dateRange={dateRange} />
                      </div>
                    </Col>
                  </Row>

                  {/* User Demographics & Activity Heatmap */}
                  <Row className="mb-3">
                    <Col xl={12}>
                      <UserDemographics dateRange={dateRange} />
                    </Col>
                  </Row>
                  <Row className="mb-3">
                    <Col xl={12}>
                      <ActivityHeatmap 
                        dateRange={dateRange} 
                        groupBy={groupBy}
                      />
                    </Col>
                  </Row>

                  {/* Match Analytics */}
                  <Row className="mb-3">
                    <Col xl={12}>
                      <MatchAnalytics 
                        dateRange={dateRange} 
                        groupBy={groupBy}
                      />
                    </Col>
                  </Row>
                </div>
              </Col>

              {/* Right Sidebar - Recent Activity */}
              {rightColumn && (
                <Col xl={3}>
                  <RecentActivity 
                    dashboardData={dashboardData}
                    connectionStatus={connectionStatus}
                    error={error}
                    // Pass user details modal handlers
                    userDetailsHandlers={{
                      fetchUserDetails,
                      userDetailsModal,
                      setUserDetailsModal,
                      selectedUser
                    }}
                  />
                </Col>
              )}
            </Row>
          </ErrorBoundary>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default DashboardEcommerce;
